import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import { preFixImgUrl } from '@/utils'
import classNames from 'classnames'
import { Spin } from 'antd'
import { apiCouponPage, MiniCouponResponse } from '@/service/api/couponPage'
import { CateringCategoryCodeEnum } from '@/service/api/types'

const PAGE_SIZE = 9;

const CouponList = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [coupons, setCoupons] = useState<MiniCouponResponse[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const controllerRef = useRef<AbortController>(new AbortController());
  const pageCurrent = useRef(1);
  const waterfallRef = useRef<HTMLDivElement>(null);
  // 滚动到锚点
  const scrollToCouponList = (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.preventDefault();
    const element = document.getElementById('coupon-list');
    if (element) {
      const offset = 150;
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo({
        top: rect.top + scrollTop - offset,
        behavior: 'instant'
      });
    }
  };

  useLayoutEffect(() => {
    const loadingMore = document.getElementById('loading-more');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          fetchCoupons();
        }
      });
    });
    loadingMore && observer.observe(loadingMore);
    return () => {
      observer.disconnect();
    }
  }, [hasMore])

  const fetchCoupons = () => {
    setLoading(true);
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
    apiCouponPage({
      current: pageCurrent.current,
      size: PAGE_SIZE,
      categoryCode: activeTab,
    }, {
      signal: controllerRef.current.signal,
    }).then(res => {
      if (res.code === 0) {
        if (pageCurrent.current === 1) {
          setCoupons(res.data.records);
          const startAnchor = document.getElementById('start-anchor');
          if (startAnchor) {
            const rect = startAnchor.getBoundingClientRect();
            const inViewport = rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
            if (!inViewport) {
              scrollToCouponList()
            }
          }
        } else {
          setCoupons(prev => [...prev, ...res.data.records]);
        }

        if (res.data.total > pageCurrent.current * PAGE_SIZE) {
          setHasMore(true);
          pageCurrent.current++
        } else {
          setHasMore(false);
        }
      } else {
        setCoupons([]);
      }
    }).finally(() => {
      setLoading(false);
    });
  }

  useEffect(() => {
    pageCurrent.current = 1;
    fetchCoupons();
  }, [activeTab])


  const tagBgColorMap = useMemo(() => {
    return {
      [CateringCategoryCodeEnum.FOOD]: 'bg-[#FC844C]',
      [CateringCategoryCodeEnum.SHOPPING]: 'bg-[#FC504C]',
      [CateringCategoryCodeEnum.ENTERTAINMENT]: 'bg-[#66AD27]',
      [CateringCategoryCodeEnum.TRANSPORT]: 'bg-[#4C97FC]',
    }
  }, [])

  const tagNameMap = useMemo(() => {
    return {
      [CateringCategoryCodeEnum.FOOD]: '727abe5d',
      [CateringCategoryCodeEnum.SHOPPING]: '7a7a98a7',
      [CateringCategoryCodeEnum.ENTERTAINMENT]: '92b0cd80',
      [CateringCategoryCodeEnum.TRANSPORT]: 'c3d567d5',
    }
  }, [])

  const tabOptions = useMemo(() => {
    return [
      {
        nameKey: 'b0569342',
        value: '',
      },
      {
        nameKey: '6f94e974',
        value: CateringCategoryCodeEnum.FOOD
      },
      {
        nameKey: 'f147168e',
        value: CateringCategoryCodeEnum.SHOPPING
      },
      {
        nameKey: 'e666eb28',
        value: CateringCategoryCodeEnum.ENTERTAINMENT
      },
      {
        nameKey: '3c9168ed',
        value: CateringCategoryCodeEnum.TRANSPORT
      },
    ]
  }, [tagNameMap])

  const onImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const height = (e.target.parentNode.parentNode as HTMLElement).offsetHeight + 30;
    e.target.parentNode.parentNode.parentNode.style.gridRow = `span ${~~(height)}`
  }


  return (
    <div>
      <div className='h-[800px] bg-cover bg-center bg-no-repeat flex items-center justify-center' style={{ backgroundImage: `url(${preFixImgUrl('52ffc54e86e600d77828b29d411c1994.png')})` }}>
        <div className='w-8xl mx-auto px-10'>
          <div className='bg-[#FFF8F0] rounded-[20px] px-[60px] py-[68px] flex items-center justify-between'>
            <div className='flex flex-col gap-6'>
              <div className='rounded-[12px] rounded-bl-[0] bg-[#FF9D34] w-fit h-[35px] leading-[35px]'>
                <LangText lngKey='1b8ae42a' className='text-white text-[18px] px-3' />
              </div>
              <LangText lngKey='cf14d89b' className='text-[44px] font-bold text-black' />
              <LangText lngKey='075dace4' className='text-[18px] text-[#666666] w-[450px] leading-[1.5]' />
              <div
                onClick={(e: React.MouseEvent<HTMLDivElement>) => scrollToCouponList(e)}
                className='h-[47px] px-[35px] flex items-center justify-center rounded-[24px] w-fit text-[18px] bg-linear-to-r from-[#FD8A0F] to-[#FFA632] text-white cursor-pointer transition-opacity hover:opacity-90'
              >
                <LangText lngKey='bea73115' />
              </div>
            </div>
            <div className='w-[484px] h-[353px]  rounded-[34px] flex flex-col justify-center'>
              <img src={preFixImgUrl('5d78589e2e2cc280e0d7c4485df2f708.png')} className='h-[340px]' />
            </div>
          </div>
        </div>
      </div>
      <div className='w-8xl mx-auto px-10  pb-[50px]' id='coupon-list'>
        <div id='start-anchor' className='h-[1px]'></div>
        <div className='sticky top-[100px] z-10 flex items-center gap-4 justify-between h-25 rounded-[50px] bg-white px-[50px] mt-[-50px] shadow-[0px_10px_20px_0px_#F4F5F8]'>
          {
            tabOptions.map(item => (
              <div
                key={item.nameKey}
                onClick={() => setActiveTab(item.value)}
                className={classNames('min-w-[180px] h-[47px] px-[35px] flex items-center justify-center rounded-[24px] w-fit text-[18px]  cursor-pointer', activeTab === item.value && 'bg-[#FF9D34] text-white')}>
                <LangText lngKey={item.nameKey} className={classNames(activeTab === item.value ? 'text-white' : 'text-black')}>{t(item.nameKey)}</LangText>
              </div>
            ))
          }
        </div>
        <div
          ref={waterfallRef}
          className='relative mt-[82px] z-1 grid grid-cols-3 gap-[30px] auto-rows-[1px] gap-x-[30px] gap-y-0'
          style={{ minHeight: '800px' }}
        >
          {
            coupons.map((item, index) => (
              <div className='row-start-[span_500] row-end-auto'>
                <div key={item.id} className='bg-white rounded-[24px] px-6 relative overflow-hidden'>
                  <div className={classNames('absolute top-0 left-0 rounded-br-[24px] px-[10px] h-[42px] flex items-center z-10', tagBgColorMap[item.categoryCode])}>
                    <LangText lngKey={tagNameMap[item.categoryCode]} className='text-white' />
                  </div>
                  <div className='flex flex-col items-center pt-[50px] pb-[30px]'>
                    <span className='text-[30px] text-black font-bold truncate max-w-full' title={item.shopName}>{item.shopName}</span>
                    <img
                      src={item.imageUrl}
                      className='w-full mt-8 object-cover'
                      alt={item.shopName}
                      onLoad={onImgLoad}
                      onError={onImgLoad}
                    />
                  </div>
                </div>
              </div>
            ))
          }
        </div>
        {
          hasMore && <Spin spinning={loading}>
            <div className='flex items-center justify-center h-[50px] w-full' id='loading-more' />
          </Spin>
        }

      </div>
    </div>
  )
}

export default CouponList